import React, { useState, useEffect, useCallback } from 'react';
import './SuccessToast.css';

interface SuccessToastProps {
  message: string;
  onDismiss: () => void;
  autoHide?: boolean;
  autoHideDelay?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  className?: string;
}

export const SuccessToast: React.FC<SuccessToastProps> = ({
  message,
  onDismiss,
  autoHide = true,
  autoHideDelay = 6000, // Slightly longer for success messages
  position = 'top-center',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss();
    }, 300); // Wait for exit animation
  }, [onDismiss]);

  useEffect(() => {
    if (!autoHide) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, autoHideDelay - elapsed);
      const progressPercent = (remaining / autoHideDelay) * 100;
      
      setProgress(progressPercent);
      
      if (remaining <= 0) {
        handleDismiss();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [autoHide, autoHideDelay, handleDismiss]);

  const containerClasses = [
    'success-toast',
    `success-toast--${position}`,
    isVisible ? 'success-toast--visible' : 'success-toast--hidden',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={containerClasses}
      role="alert"
      aria-live="polite"
    >
      <div className="success-toast__content">
        {/* Icon */}
        <div className="success-toast__icon" aria-hidden="true">
          ✅
        </div>

        {/* Message */}
        <div className="success-toast__message">
          {message}
        </div>

        {/* Dismiss Button */}
        <button
          type="button"
          className="success-toast__dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss notification"
        >
          ✕
        </button>
      </div>

      {/* Progress bar for auto-hide */}
      {autoHide && (
        <div 
          className="success-toast__progress"
          style={{ width: `${progress}%` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};